import React from 'react';
import styled, { css, Palette, PaletteVariants, Sizes } from 'styled-components';

export type CheckboxIconColors = `primary` | `secondary`;

export type CheckboxIconProp = {
  checkboxIconColor?: CheckboxIconColors;
};

type BoxProps = {
  active?: boolean;
} & CheckboxIconProp;

type CheckProps = {
  checked?: boolean;
  indeterminate?: boolean;
} & CheckboxIconProp;

type CheckboxProps = {
  size?: Sizes;
  borderColor?: Palette;
  borderColorShade?: PaletteVariants;
} & CheckProps;

type CheckboxStyledProps = {
  size: Sizes;
  borderColor: Palette;
  borderColorShade: PaletteVariants;
} & BoxProps;

const CheckBox = styled.div<CheckboxStyledProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => {
    if (props.checkboxIconColor === 'secondary') {
      return props.theme.palette.greyBlack.main;
    }

    if (props.active) {
      return props.theme.palette.primary.main;
    }

    return '#fff';
  }};
  border-radius: ${(props) => props.theme.roundedCorners.xsmall};
  box-shadow: ${(props) => props.theme.shadows.small};
  border: 1px solid ${(props) => props.theme.palette[props.borderColor][props.borderColorShade]};
  ${(props) => props.active && `border-color: transparent;`}
  width: ${(props) => props.theme.sizes[props.size]};
  height: ${(props) => props.theme.sizes[props.size]};
`;

const IconStyling = css`
  position: absolute;
  width: 0.75rem;
  height: 0.625rem;
  stroke: ${(props) => props.theme.palette.white.main};
  stroke-width: 1.5;
  fill: none;
`;

const CheckSvg = styled.svg<CheckProps>`
  ${IconStyling};
  opacity: ${(props) => (props.checked ? 1 : 0)};
`;

const IndeterminateSvg = styled.svg<CheckProps>`
  ${IconStyling};
  opacity: ${(props) => (props.indeterminate ? 1 : 0)};
`;

export const Check: React.FunctionComponent<CheckboxProps> = ({
  checked,
  indeterminate,
  checkboxIconColor = `primary`,
  size = 'medium',
  borderColor = 'primary',
  borderColorShade = '300',
}) => (
  <CheckBox
    active={checked || indeterminate}
    checkboxIconColor={checkboxIconColor}
    size={size}
    borderColor={borderColor}
    borderColorShade={borderColorShade}
  >
    <CheckSvg checked={checked}>
      <path
        d="M1 4.755L5.234 9 11 1"
        fillRule="evenodd"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </CheckSvg>
    <IndeterminateSvg indeterminate={indeterminate}>
      <path d="M1,5 L11,5" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round" />
    </IndeterminateSvg>
  </CheckBox>
);
